/**
 * React hook for checking feature flags.
 */

import { useEffect, useState } from 'react';
import { PostHogManager } from '#~/posthog/manager.js';
import { resolveFeatureFlagFallback } from '#~/posthog/constants.js';
import type { FeatureFlagOptions } from '#~/posthog/types.js';

/**
 * Hook to get a single feature flag value.
 *
 * @param flag - Feature flag name
 * @param options - Options for flag retrieval
 * @returns Object with flag value (undefined while loading), loading state, and error
 *
 * @example
 * ```typescript
 * const { enabled, loading, error } = useFeatureFlag('AUTOTRACKING', {
 *   defaultValue: false,
 *   timeout: 5000
 * });
 *
 * if (loading) return <Spinner />;
 * if (enabled) return <AutoTracking />;
 * return null;
 * ```
 */
export function useFeatureFlag(
  flag: string,
  options?: {
    defaultValue?: boolean;
    timeout?: number;
    enableRealtime?: boolean; // New option
  }
) {
  const [enabled, setEnabled] = useState<boolean | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number>(Date.now()); // New state

  useEffect(() => {
    let mounted = true;
    let unsubscribe: (() => void) | undefined;

    const manager = PostHogManager.getInstance();

    // Initial load
    const flagOptions: FeatureFlagOptions = {};
    if (options?.defaultValue !== undefined) flagOptions.defaultValue = options.defaultValue;
    if (options?.timeout !== undefined) flagOptions.timeout = options.timeout;
    if (options?.enableRealtime !== undefined) flagOptions.enableRealtime = options.enableRealtime;

    manager
      .getFeatureFlag(flag, flagOptions)
      .then((result) => {
        if (mounted) {
          setEnabled(result);
          setLoading(false);
          setLastUpdated(Date.now());
        }
      })
      .catch((err) => {
        if (mounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
          setEnabled(resolveFeatureFlagFallback(flag, options?.defaultValue));
        }
      });

    // Set up real-time subscription (unless disabled)
    if (options?.enableRealtime !== false) {
      unsubscribe = manager.subscribeToFlag(flag, (value) => {
        if (mounted) {
          setEnabled(value);
          setLastUpdated(Date.now());
          // Clear any previous errors since we got an update
          setError(null);
        }
      });
    }

    return () => {
      mounted = false;
      unsubscribe?.();
    };
  }, [flag, options?.defaultValue, options?.timeout, options?.enableRealtime]);

  return {
    /**
     * Whether the feature flag is enabled once loading completes.
     * Returns undefined while the flag value is loading.
     */
    enabled,

    /**
     * Whether the flag is still loading.
     */
    loading,

    /**
     * Error if flag retrieval failed.
     */
    error,

    /**
     * Timestamp of the last update.
     */
    lastUpdated
  };
}
